import { supabase, auth } from './supabase';
import type { UserProfile } from '../types';

const LOCAL_CREDITS_KEY = 'echo_scribe_credits';
const INITIAL_CREDITS = 10 * 60 * 1000; // 10 minutes in ms

// ── Credits ───────────────────────────────────────────────────────────────────

export const getUserCredits = async (): Promise<number> => {
  const user = auth.currentUser;
  if (user) {
    const { data, error } = await supabase
      .from('users')
      .select('credits')
      .eq('id', user.id)
      .single();
    if (error || !data) return INITIAL_CREDITS;
    return typeof data.credits === 'number' ? data.credits : INITIAL_CREDITS;
  }
  const local = localStorage.getItem(LOCAL_CREDITS_KEY);
  if (local !== null) return parseInt(local, 10);
  localStorage.setItem(LOCAL_CREDITS_KEY, INITIAL_CREDITS.toString());
  return INITIAL_CREDITS;
};

export const getUserProfile = async (): Promise<{ credits: number; isPaid: boolean } | null> => {
  const user = auth.currentUser;
  if (!user) return null;
  const { data, error } = await supabase
    .from('users')
    .select('credits, is_paid')
    .eq('id', user.id)
    .single();
  if (error || !data) return null;
  return {
    credits: typeof data.credits === 'number' ? data.credits : INITIAL_CREDITS,
    isPaid: !!data.is_paid,
  };
};

export const deductUserCredits = async (amountMs: number): Promise<number> => {
  const user = auth.currentUser;
  if (user) {
    const { data, error } = await supabase.rpc('deduct_credits', { amount: amountMs });
    if (error) throw error;
    return typeof data === 'number' ? data : 0;
  }
  const current = await getUserCredits();
  const newValue = Math.max(0, current - amountMs);
  localStorage.setItem(LOCAL_CREDITS_KEY, newValue.toString());
  return newValue;
};

// ── Profile subscription (Realtime) ──────────────────────────────────────────

export const subscribeToUserProfile = (
  uid: string,
  onUpdate: (profile: Partial<UserProfile>) => void,
): () => void => {
  const channel = supabase
    .channel(`user-profile-${uid}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'users', filter: `id=eq.${uid}` },
      (payload) => {
        const row = payload.new as Record<string, unknown>;
        onUpdate({
          credits: row.credits as number,
          isPaid: row.is_paid as boolean,
          plan: row.plan as 'free' | 'lifetime',
          changelogReadAt: row.changelog_read_at as number | undefined,
        });
      },
    )
    .subscribe();

  return () => { supabase.removeChannel(channel); };
};

// ── Account deletion ──────────────────────────────────────────────────────────

export const deleteAccount = async (): Promise<void> => {
  const { error } = await supabase.rpc('delete_user');
  if (error) throw error;
  await supabase.auth.signOut();
};
